import { useAuthState } from '@auth/hooks';
import { useUpdateCommunity } from '@communities/community/hooks';
import { useUpdateCommunityGroup } from '@communities/community-groups/hooks';
import { HeroBackground, SectionHeader } from '@core/components';
import type { CommunityGroupWithCommunities } from '@core/components/Guide';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Container, PageHeader, Section } from '@soteria/design-system-ve';
import { EditCommunitySettingDialog } from '@system-config/community-management/components';
import { useCommunityManagement, type CommunityWithGroupName } from '@system-config/community-management/hooks';
import { SortableDataList, type SortableTableColumn, type SortOption } from '@system-config/label-management/components/SortableDataList';
import { useCallback, useState } from 'react';

import * as styles from './CommunityManagementPage.css';

// --- Column & sort definitions ---

const groupColumns: SortableTableColumn<CommunityGroupWithCommunities>[] = [
  { key: 'name', header: 'Name', render: item => item.name, width: '35%', sortable: true, sortKey: 'name' },
  {
    key: 'description',
    header: 'Description',
    render: item => item.description || '-',
    width: '45%',
    sortable: true,
    sortKey: 'description',
  },
  {
    key: 'communityCount',
    header: 'Communities',
    render: item => String(item.communities.length),
    width: '20%',
    sortable: true,
    sortKey: (item: CommunityGroupWithCommunities) => item.communities.length,
  },
];

const groupSortOptions: SortOption<CommunityGroupWithCommunities>[] = [
  { value: 'name', label: 'Name', sortKey: 'name' },
  { value: 'description', label: 'Description', sortKey: 'description' },
  { value: 'communityCount', label: 'Communities', sortKey: (item: CommunityGroupWithCommunities) => item.communities.length },
];

const communityColumns: SortableTableColumn<CommunityWithGroupName>[] = [
  { key: 'name', header: 'Name', render: item => item.name, width: '50%', sortable: true, sortKey: 'name' },
  {
    key: 'groupName',
    header: 'Community Group',
    render: item => item.groupName,
    width: '50%',
    sortable: true,
    sortKey: 'groupName',
  },
];

const communitySortOptions: SortOption<CommunityWithGroupName>[] = [
  { value: 'name', label: 'Name', sortKey: 'name' },
  { value: 'groupName', label: 'Community Group', sortKey: 'groupName' },
];

// --- Page component ---

function CommunityManagementPageComponent() {
  const { isUserSystemAdmin } = useAuthState();

  const {
    communityGroups,
    groupsLoading,
    groupsErrors,
    groupDeleteItemContext,
    communities,
    communitiesLoading,
    communitiesErrors,
    communityDeleteItemContext,
    refetch,
  } = useCommunityManagement();

  // Rename dialog state
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<{ id: string; name: string; type: 'community' | 'community group' } | null>(null);

  const { updateCommunity, isUpdating: communityUpdateLoading, errors: communityUpdateErrors, resetErrors: resetCommunityErrors } = useUpdateCommunity();
  const { updateCommunityGroup, loading: groupUpdateLoading, errors: groupUpdateErrors, resetErrors: resetGroupErrors } = useUpdateCommunityGroup();

  const editLoading = communityUpdateLoading || groupUpdateLoading;
  const editErrors = communityUpdateErrors || groupUpdateErrors;

  const resetEditErrors = useCallback(() => {
    resetCommunityErrors();
    resetGroupErrors();
  }, [resetCommunityErrors, resetGroupErrors]);

  const handleEditGroup = useCallback((group: CommunityGroupWithCommunities) => {
    setRenameTarget({ id: group.id, name: group.name, type: 'community group' });
    setIsRenameOpen(true);
  }, []);

  const handleEditCommunity = useCallback((community: CommunityWithGroupName) => {
    setRenameTarget({ id: community.id, name: community.name, type: 'community' });
    setIsRenameOpen(true);
  }, []);

  const handleCloseRename = useCallback(() => {
    resetEditErrors();
    setIsRenameOpen(false);
    setRenameTarget(null);
  }, [resetEditErrors]);

  const handleSaveRename = useCallback(
    async (newName: string): Promise<boolean> => {
      if (!renameTarget) return false;

      const success =
        renameTarget.type === 'community'
          ? await updateCommunity(renameTarget.id, { name: newName })
          : await updateCommunityGroup(renameTarget.id, { name: newName });

      if (success) {
        try {
          await refetch();
        } catch {
          // Mutation succeeded — swallow refetch errors so the dialog closes normally
        }
      }

      return success;
    },
    [renameTarget, updateCommunity, updateCommunityGroup, refetch]
  );

  if (!isUserSystemAdmin) {
    return null;
  }

  return (
    <>
      <HeroBackground />
      <PageHeader data-testid="community-management-header" subtitle="Manage community groups and communities" title="Community Management" />
      <Container className={styles.withHeroBackground} padding="xxlarge" size="xxl">
        {/* Community Groups section */}
        <Section spacing="large">
          <SectionHeader description="Manage community group deletion" title="Community Groups" />
          <SortableDataList
            enablePagination
            searchable
            showFilterBar
            columns={groupColumns}
            data={communityGroups}
            dataTypeName="community group"
            defaultPageSize={10}
            defaultSort={{ key: 'name', direction: 'asc' }}
            deleteItemContext={groupDeleteItemContext}
            error={groupsErrors}
            isLoading={groupsLoading}
            pageSizeOptions={[10, 25, 50]}
            searchKey="name"
            searchPlaceholder="Search community groups by name..."
            sortOptions={groupSortOptions}
            onEdit={handleEditGroup}
          />
        </Section>

        {/* Communities section */}
        <Section spacing="large">
          <SectionHeader description="Manage community deletion" title="Communities" />
          <SortableDataList
            enablePagination
            searchable
            showFilterBar
            columns={communityColumns}
            data={communities}
            dataTypeName="community"
            defaultPageSize={10}
            defaultSort={{ key: 'name', direction: 'asc' }}
            deleteItemContext={communityDeleteItemContext}
            error={communitiesErrors}
            isLoading={communitiesLoading}
            pageSizeOptions={[10, 25, 50]}
            searchKey="name"
            searchPlaceholder="Search communities by name..."
            sortOptions={communitySortOptions}
            onEdit={handleEditCommunity}
          />
        </Section>
      </Container>

      {renameTarget && (
        <EditCommunitySettingDialog
          currentName={renameTarget.name}
          errors={editErrors}
          isOpen={isRenameOpen}
          itemType={renameTarget.type}
          loading={editLoading}
          resetErrors={resetEditErrors}
          onClose={handleCloseRename}
          onSave={handleSaveRename}
        />
      )}
    </>
  );
}

export const CommunityManagementPage = withAppInsightsTracking(CommunityManagementPageComponent, 'CommunityManagementPage');
